import axios from 'axios';
import {
  AdBlockerPopup,
  Breadcrumb,
  CommunitySlotsModal,
  Loader,
  Stepper,
} from 'components';
import { OutsideAppPageContentWrapper } from 'components/Shared/styled';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import StripeCheckoutOutcome from 'pages/UserProfileSubscription/StripeCheckoutOutcome';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getLocation } from 'services/geolocation';
import {
  buildStripeCheckoutPresentmentBody,
  createInvoiceSubscriptionRequest,
  createStripeCheckoutSessionRequest,
  postChangePlanRequest,
} from 'services/subscriptions';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import {
  selectAllSubscriptions,
  selectCurrentUserSubscription,
  selectInvoiceBillingSource,
  selectIsSubscriptionLoading,
} from 'store/selectors/subscriptions';
import {
  SubscriptionFrequency,
  SubscriptionPlan,
  SubscriptionStatus,
} from 'types';
import { addMonths, formatSubscriptionDate } from 'utils/dates';
import { isTrialSubscription } from 'utils/subscriptionFreePeriod';
import {
  InvoiceBillingNotAllowedError,
  mustResubscribeWithDirectPayment,
} from 'utils/subscriptionState';

import { buildInvoiceBillingDefaults } from './invoiceBillingDefaults';
import InvoiceChfBillingFields, {
  type InvoiceBillingData,
  type InvoiceChfBillingHandle,
  MAX_BILLING_NAME_LENGTH,
} from './InvoiceChfBillingFields';
import InvoicePayerEmailsField, {
  type InvoicePayerEmailsFieldHandle,
} from './InvoicePayerEmailsField';
import {
  isYupValidationError,
  validateInvoicePayerEmails,
} from './invoicePayerEmailsSchema';
import {
  InvoiceDocIcon,
  MastercardIcon,
  SepaIcon,
  StripeIcon,
  StripeWordmark,
  VisaIcon,
} from './PaymentBrandIcons';
import * as Ch from './styled';
import { useCheckoutProration } from './useCheckoutProration';

type CheckoutMoneyProps = {
  ready: boolean;
  skeletonWidth?: string;
  skeletonHeight?: number;
  children?: React.ReactNode;
};

const CheckoutMoney = ({
  ready,
  skeletonWidth = '4.5rem',
  skeletonHeight = 14,
  children,
}: CheckoutMoneyProps) => {
  if (!ready) {
    return (
      <span
        className="skeleton skeleton-text"
        style={{
          display: 'inline-block',
          width: skeletonWidth,
          height: skeletonHeight,
          verticalAlign: 'middle',
        }}
        aria-hidden
      />
    );
  }
  return <>{children}</>;
};

const formatCheckoutAmount = (value: number | string) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return String(value);
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

type SubscriptionInnerProps = {
  planFallback?: SubscriptionPlan | null;
  initialActiveCommunityIds?: string[];
};

const SubscriptionInner = ({
  planFallback = null,
  initialActiveCommunityIds,
}: SubscriptionInnerProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscriptionId: currentSubscriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const subscriptions = useSelector(selectAllSubscriptions);
  const activeSubscription = useSelector(selectCurrentUserSubscription);
  const savedInvoiceBilling = useSelector(selectInvoiceBillingSource);
  const isSubscriptionLoading = useSelector(selectIsSubscriptionLoading);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const [hasAdBlocker, setHasAdBlocker] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<'direct' | 'invoice'>(
    'direct',
  );
  const invoicePaymentDisabled =
    mustResubscribeWithDirectPayment(activeSubscription);
  // Derived lock: React DevTools flipping `paymentMethod` cannot select invoice.
  const effectivePaymentMethod: 'direct' | 'invoice' = invoicePaymentDisabled
    ? 'direct'
    : paymentMethod;

  useEffect(() => {
    if (invoicePaymentDisabled && paymentMethod !== 'direct') {
      setPaymentMethod('direct');
    }
  }, [invoicePaymentDisabled, paymentMethod]);

  const [payerEmailRows, setPayerEmailRows] = useState<string[]>([]);
  const payerEmailsSeededRef = useRef(false);
  const payerEmailsFieldRef = useRef<InvoicePayerEmailsFieldHandle>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [invoiceBillingReady, setInvoiceBillingReady] = useState(false);
  const invoiceBillingRef = useRef<InvoiceChfBillingHandle>(null);
  const [directCheckoutLoading, setDirectCheckoutLoading] = useState(false);
  const [directCheckoutError, setDirectCheckoutError] = useState<string | null>(
    null,
  );
  const [invoiceResult, setInvoiceResult] = useState<{
    hostedInvoiceUrl: string | null;
    amountLabel?: string | null;
  } | null>(null);
  /** Change-plan with no Stripe redirect (e.g. downgrade) — show confirmation step inline. */
  const [planChangeConfirmed, setPlanChangeConfirmed] = useState(false);
  const [communitySlotsOpen, setCommunitySlotsOpen] = useState(false);
  const [activeCommunityIds, setActiveCommunityIds] = useState<string[] | null>(
    initialActiveCommunityIds?.length ? initialActiveCommunityIds : null,
  );
  const [hasObservedSubscriptionLoad, setHasObservedSubscriptionLoad] =
    useState(false);

  const frequency = searchParams.get('frequency');

  const currentUserSubscriptionFrequency = activeSubscription?.frequency;
  const currentUserSubscriptionStatus = activeSubscription?.status;
  const currentUserSubscription = useMemo(() => {
    if (planFallback && planFallback.id === currentSubscriptionId) {
      return planFallback;
    }
    return subscriptions?.find(({ id }) => id === currentSubscriptionId);
  }, [subscriptions, currentSubscriptionId, planFallback]);

  const currentPlan = currentUserSubscription as SubscriptionPlan;
  const {
    maxGroups,
    maxMembers,
    maxCommunities,
    maxStorage,
    currency,
    monthlyValue,
    yearlyValue,
    name,
    productName,
    tax,
  } = currentPlan;

  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;
  const displayPlanName = productName || name;

  const isCustom = name.includes('Enterprise');

  const isCurrent =
    currentUserSubscriptionStatus !== SubscriptionStatus.CANCELLED &&
    currentUserSubscriptionStatus !== SubscriptionStatus.TRIAL_EXPIRED &&
    currentSubscriptionId === activeSubscription?.subscriptionPlanId &&
    currentUserSubscriptionFrequency === frequency;
  const currentPrice =
    frequency === SubscriptionFrequency.YEAR ? yearlyValue : monthlyValue;

  const displayPayButton = useMemo(() => {
    if (!isCurrent) {
      return true;
    }
    if (
      new Date().getTime() >
        new Date(activeSubscription?.endDate || null).getTime() ||
      (activeSubscription?.status === SubscriptionStatus.ACTIVE &&
        activeSubscription?.planId === SubscriptionStatus.MIGRATED)
    ) {
      return true;
    }
    return false;
  }, [isCurrent, activeSubscription]);

  const needsSubscriptionResolution =
    displayPayButton && !isCurrent && !isCustom;

  const isSubscriptionPending = useMemo(() => {
    if (!needsSubscriptionResolution) return false;
    if (activeSubscription) return isSubscriptionLoading;
    return isSubscriptionLoading || !hasObservedSubscriptionLoad;
  }, [
    needsSubscriptionResolution,
    activeSubscription,
    isSubscriptionLoading,
    hasObservedSubscriptionLoad,
  ]);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  useEffect(() => {
    if (isSubscriptionLoading) {
      setHasObservedSubscriptionLoad(true);
    }
  }, [isSubscriptionLoading]);

  const ownedCommunitiesList = useMemo(() => {
    if (!profile?.userId) return [];
    return communities
      .filter((c) => c.communityOwner?.userId === profile.userId)
      .map((c) => ({
        id: c.id,
        name: c.name ?? '',
        logo: c.logo ?? null,
        subscriptionSlotActive: c.subscriptionSlotActive,
      }));
  }, [communities, profile?.userId]);

  const activeCommunitiesForSlots =
    activeSubscription?.info?.activeCommunities ??
    activeSubscription?.info?.communities ??
    0;

  const needsCommunitySlotsChoice = useMemo(() => {
    const max = maxCommunities ?? 0;
    if (!profile?.userId || max <= 0) return false;
    return activeCommunitiesForSlots > max;
  }, [activeCommunitiesForSlots, maxCommunities, profile?.userId]);

  const pendingSlotSelection = activeCommunityIds;

  useEffect(() => {
    if (!needsCommunitySlotsChoice || !displayPayButton) return;
    if (pendingSlotSelection?.length) return;
    setCommunitySlotsOpen(true);
  }, [needsCommunitySlotsChoice, displayPayButton, pendingSlotSelection]);

  const expirationDate = useMemo(() => {
    if (displayPayButton) {
      const now = new Date();
      const isOnTrial = isTrialSubscription(activeSubscription);

      if (isOnTrial && activeSubscription?.endDate) {
        const trialEnd = new Date(activeSubscription.endDate);
        if (trialEnd.getTime() > now.getTime()) {
          return trialEnd;
        }
      }

      return now;
    }

    return new Date(activeSubscription.endDate);
  }, [displayPayButton, activeSubscription]);

  const yearlySaveVsMonthly = useMemo(
    () => monthlyValue * 12 - yearlyValue,
    [monthlyValue, yearlyValue],
  );

  const freePeriodLabel = t('changePlan.saveBadge');

  const priceSaveVsMonthlyLabel = t(
    'subscriptions.checkout.priceSaveVsMonthly',
    { period: freePeriodLabel },
  );

  const {
    isUpgradeCheckout,
    proration,
    pricesReady,
    isCheckoutDetailsLoading,
    fromPlanName,
    showProrationCredit,
    showRemainingCredit,
    renewalDateFromPreview,
    planChangeDirection,
  } = useCheckoutProration({
    displayPayButton,
    checkoutPlanId: currentSubscriptionId,
    activeSubscription,
    frequency,
    currentPrice,
    isSubscriptionPending,
  });

  const displayCurrency =
    isUpgradeCheckout && proration?.currency ? proration.currency : currency;

  const currencySym =
    displayCurrency === 'EUR' ? '€' : displayCurrency === 'CHF' ? 'CHF ' : '$';

  const renewalDateStr = useMemo(() => {
    if (isUpgradeCheckout && renewalDateFromPreview) {
      return formatSubscriptionDate(
        new Date(renewalDateFromPreview),
        i18n.language,
      );
    }
    const renewal: Date =
      frequency === SubscriptionFrequency.MONTH
        ? displayPayButton
          ? addMonths(expirationDate, 1)
          : expirationDate
        : displayPayButton
        ? addMonths(expirationDate, 12)
        : expirationDate;
    return formatSubscriptionDate(renewal, i18n.language);
  }, [
    frequency,
    displayPayButton,
    expirationDate,
    i18n.language,
    isUpgradeCheckout,
    renewalDateFromPreview,
  ]);

  const linePriceForDisplay =
    isUpgradeCheckout && proration ? proration.planLinePrice : currentPrice;

  const displayedLinePrice = useMemo(() => {
    if (tax > 0) {
      return formatCheckoutAmount((linePriceForDisplay * (100 - tax)) / 100);
    }

    return formatCheckoutAmount(linePriceForDisplay);
  }, [linePriceForDisplay, tax]);

  const planLineSubtext = useMemo(() => {
    const days = proration?.daysRemaining;
    if (!isUpgradeCheckout || days == null || days <= 0) {
      return null;
    }

    const price = formatCheckoutAmount(proration.planLinePrice);

    if (frequency === SubscriptionFrequency.YEAR) {
      return t('subscriptions.checkout.planLineSubtextAnnual', {
        currency: currencySym,
        price,
        days,
      });
    }

    return t('subscriptions.checkout.planLineSubtextMonthly', {
      currency: currencySym,
      price,
      days,
    });
  }, [
    currencySym,
    frequency,
    isUpgradeCheckout,
    proration?.daysRemaining,
    proration?.planLinePrice,
    t,
  ]);

  const previousPlanLineSubtext = useMemo(() => {
    const days = proration?.daysRemaining;
    if (!showProrationCredit || days == null || days <= 0) {
      return null;
    }

    return t('subscriptions.checkout.daysRemainingSubtext', { days });
  }, [proration?.daysRemaining, showProrationCredit, t]);

  const dueToday = useMemo(() => {
    const amount =
      isUpgradeCheckout && proration ? proration.totalDueToday : currentPrice;
    return Math.max(0, amount);
  }, [isUpgradeCheckout, proration, currentPrice]);

  const formatCreditAmount = useCallback(
    (amount: number) => `−${currencySym}${formatCheckoutAmount(amount)}`,
    [currencySym],
  );

  const formatRemainingCreditAmount = useCallback(
    (amount: number) => `${currencySym}${formatCheckoutAmount(amount)}`,
    [currencySym],
  );

  const approxMonthlyEq = useMemo(() => {
    if (frequency !== SubscriptionFrequency.YEAR || !yearlyValue) return null;
    return Math.round((yearlyValue / 12) * 100) / 100;
  }, [frequency, yearlyValue]);

  const planEmoji = isCustom ? '🏢' : '🚀';

  const includedItems = useMemo(
    () =>
      (
        [
          'subscriptions.checkout.included1',
          'subscriptions.checkout.included2',
          'subscriptions.checkout.included3',
          'subscriptions.checkout.included4',
          'subscriptions.checkout.included5',
          'subscriptions.checkout.included6',
          'subscriptions.checkout.included7',
          'subscriptions.checkout.included8',
        ] as const
      ).map((key) => t(key)),
    [t],
  );

  const showInvoiceForm =
    displayPayButton &&
    frequency === SubscriptionFrequency.YEAR &&
    !invoicePaymentDisabled;

  const requiresChfInvoiceBilling =
    showInvoiceForm &&
    effectivePaymentMethod === 'invoice' &&
    displayCurrency === 'CHF';

  /** QR-Bill debtor name fallback when subscription has no saved billing name. */
  const fallbackBillingName = useMemo(() => {
    const selectedId = pendingSlotSelection?.[0];
    const selected = selectedId
      ? ownedCommunitiesList.find((c) => c.id === selectedId)
      : null;
    const communityName = selected?.name || ownedCommunitiesList[0]?.name || '';
    return communityName.slice(0, MAX_BILLING_NAME_LENGTH);
  }, [ownedCommunitiesList, pendingSlotSelection]);

  const invoiceBillingDefaults = useMemo(
    () =>
      buildInvoiceBillingDefaults({
        source: savedInvoiceBilling,
        fallbackName: fallbackBillingName,
        defaultCountry: 'CH',
      }),
    [savedInvoiceBilling, fallbackBillingName],
  );

  // Seed non-CHF payer email rows once from saved subscription emails.
  useEffect(() => {
    if (payerEmailsSeededRef.current) return;
    const emails = invoiceBillingDefaults.billingEmails;
    if (!emails.length) return;
    payerEmailsSeededRef.current = true;
    setPayerEmailRows(emails);
  }, [invoiceBillingDefaults.billingEmails]);

  const submitInvoiceSubscription = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setInvoiceError(null);
      if (invoicePaymentDisabled) {
        setPaymentMethod('direct');
        return;
      }
      if (!currentSubscriptionId) {
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
        return;
      }
      let chfBillingData: InvoiceBillingData | null = null;
      let payerEmails: string[];
      if (requiresChfInvoiceBilling) {
        // CHF collects recipients inside the billing panel, not as separate rows.
        const billingFieldsValid =
          invoiceBillingRef.current?.validateBillingFields() ?? false;
        if (!billingFieldsValid || !invoiceBillingReady) {
          setInvoiceError(null);
          return;
        }
        chfBillingData = invoiceBillingRef.current?.getBillingData() ?? null;
        if (!chfBillingData) {
          setInvoiceError(t('subscriptions.checkout.chf.addressRequired'));
          return;
        }
        payerEmails = chfBillingData.billingEmails;
      } else {
        const flushed = payerEmailsFieldRef.current?.flush();
        if (flushed?.error) {
          setInvoiceError(flushed.error);
          return;
        }
        try {
          payerEmails = validateInvoicePayerEmails(
            flushed?.emails ?? payerEmailRows,
            t,
          );
        } catch (err) {
          if (isYupValidationError(err)) {
            setInvoiceError(err.errors[0] ?? err.message);
            return;
          }
          setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
          return;
        }
      }
      if (needsCommunitySlotsChoice && !pendingSlotSelection?.length) {
        setCommunitySlotsOpen(true);
        return;
      }
      setInvoiceSubmitting(true);
      try {
        let geo: { country_code2?: string } | null = null;
        try {
          geo = await getLocation();
        } catch {
          geo = { country_code2: 'CH' };
        }
        const data = await createInvoiceSubscriptionRequest({
          subscriptionPlanId: currentSubscriptionId,
          frequency: SubscriptionFrequency.YEAR,
          payerEmails,
          ...buildStripeCheckoutPresentmentBody(geo),
          ...(pendingSlotSelection?.length
            ? { activeCommunityIds: pendingSlotSelection }
            : {}),
          ...(chfBillingData
            ? {
                billing_address: {
                  ...chfBillingData.address,
                  name: chfBillingData.name,
                },
                tax_id: chfBillingData.taxId,
              }
            : {}),
        });
        setInvoiceResult({
          hostedInvoiceUrl: data.hostedInvoiceUrl,
          amountLabel: data.amountLabel,
        });
        dispatch(getCurrentUserSubscription.trigger());
      } catch (err) {
        if (err instanceof InvoiceBillingNotAllowedError) {
          setPaymentMethod('direct');
          return;
        }
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
      } finally {
        setInvoiceSubmitting(false);
      }
    },
    [
      currentSubscriptionId,
      payerEmailRows,
      profile?.email,
      dispatch,
      needsCommunitySlotsChoice,
      pendingSlotSelection,
      requiresChfInvoiceBilling,
      invoiceBillingReady,
      invoiceBillingRef,
      invoicePaymentDisabled,
      t,
    ],
  );

  const startStripeCheckout = useCallback(async (): Promise<void | false> => {
    setDirectCheckoutError(null);

    if (needsCommunitySlotsChoice && !pendingSlotSelection?.length) {
      setCommunitySlotsOpen(true);
      return false;
    }
    if (!currentSubscriptionId) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }
    const freq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;

    if (!freq) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }

    setDirectCheckoutLoading(true);
    try {
      if (isUpgradeCheckout) {
        const data = await postChangePlanRequest({
          subscriptionPlanId: currentSubscriptionId,
          frequency: freq,
          paymentMethod: 'card',
          ...(pendingSlotSelection?.length
            ? { activeCommunityIds: pendingSlotSelection }
            : {}),
        });

        if (data?.checkoutUrl) {
          window.location.replace(data.checkoutUrl);
          return;
        }

        dispatch(getCurrentUserSubscription.trigger());
        setPlanChangeConfirmed(true);
        return;
      }

      let geo: { country_code2?: string } | null = null;
      try {
        geo = await getLocation();
      } catch {
        geo = { country_code2: 'CH' };
      }

      const data = await createStripeCheckoutSessionRequest({
        subscriptionPlanId: currentSubscriptionId,
        frequency: freq,
        ...buildStripeCheckoutPresentmentBody(geo),
      });

      if (data?.url) {
        window.location.replace(data.url);
        return;
      }

      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
    } catch (err) {
      const apiError = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      if (apiError === 'COMMUNITY_SLOTS_EXCEED_PLAN') {
        setDirectCheckoutError(null);
        setCommunitySlotsOpen(true);
        return false;
      }
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
    } finally {
      setDirectCheckoutLoading(false);
    }
  }, [
    currentSubscriptionId,
    frequency,
    dispatch,
    isUpgradeCheckout,
    needsCommunitySlotsChoice,
    pendingSlotSelection,
    t,
  ]);

  const onCommunitySlotsClose = useCallback(() => {
    setCommunitySlotsOpen(false);
  }, []);

  const onCommunitySlotsSavedCheckout = useCallback(
    async (selectedIds: string[]) => {
      setActiveCommunityIds(selectedIds);
    },
    [],
  );

  const payActionsBlock = useMemo(() => {
    if (!displayPayButton || effectivePaymentMethod !== 'direct') return null;
    const amountLabel = pricesReady
      ? `${currencySym}${formatCheckoutAmount(dueToday)}`
      : '';
    return (
      <Ch.PayActions>
        {directCheckoutError ? (
          <Ch.InvoiceFormError role="alert">
            {directCheckoutError}
          </Ch.InvoiceFormError>
        ) : null}
        <Ch.BtnPurchase
          type="button"
          disabled={directCheckoutLoading || !pricesReady}
          onClick={() => {
            void startStripeCheckout();
          }}
        >
          <Ch.BtnPurchaseInner>
            {directCheckoutLoading ? (
              <Loader type="button" size="28px" thickness={2} />
            ) : !pricesReady ? (
              <CheckoutMoney ready={false} skeletonWidth="10rem">
                {null}
              </CheckoutMoney>
            ) : (
              <>
                <span aria-hidden>🔒</span>
                {t('subscriptions.checkout.completePurchase', {
                  amount: amountLabel,
                })}
              </>
            )}
          </Ch.BtnPurchaseInner>
        </Ch.BtnPurchase>
      </Ch.PayActions>
    );
  }, [
    displayPayButton,
    effectivePaymentMethod,
    currencySym,
    dueToday,
    pricesReady,
    directCheckoutLoading,
    directCheckoutError,
    startStripeCheckout,
    t,
  ]);

  if (invoiceResult) {
    return (
      <OutsideAppPageContentWrapper minHeight="100svh">
        <StripeCheckoutOutcome
          variant="success"
          checkoutFlow="invoice"
          planLabel={displayPlanName}
          hostedInvoiceUrl={invoiceResult.hostedInvoiceUrl}
          amountLabel={invoiceResult.amountLabel}
          onDismiss={() => navigate(subscriptionPath, { replace: true })}
        />
      </OutsideAppPageContentWrapper>
    );
  }

  if (planChangeConfirmed) {
    return (
      <OutsideAppPageContentWrapper minHeight="100svh">
        <StripeCheckoutOutcome
          variant="success"
          checkoutFlow="subscription"
          planLabel={displayPlanName}
          onDismiss={() => navigate(subscriptionPath, { replace: true })}
        />
      </OutsideAppPageContentWrapper>
    );
  }

  const CheckoutPageWrapComponent = isCustom
    ? Ch.EnterpriseCheckoutPageWrap
    : Ch.CheckoutPageWrap;
  const CheckoutGridComponent = isCustom
    ? Ch.EnterpriseCheckoutLayoutGrid
    : Ch.CheckoutGrid;

  return (
    <OutsideAppPageContentWrapper minHeight="100svh">
      <AdBlockerPopup
        isOpen={hasAdBlocker}
        onClose={() => {
          setHasAdBlocker(false);
        }}
      />
      <Ch.CheckoutBackdrop>
        <CheckoutPageWrapComponent>
          <Breadcrumb
            items={[
              {
                label: t('changePlan.breadcrumbSubscription'),
                to: subscriptionPath,
              },
              {
                label: t('subscriptions.checkout.breadcrumbCheckout', {
                  plan: displayPlanName,
                }),
              },
            ]}
          />

          <Stepper
            steps={[
              {
                label: t('subscriptions.checkout.stepPlanSelected'),
                status: 'done',
                to: changePlanPath,
              },
              {
                label: t('subscriptions.checkout.stepOrderSummary'),
                status: 'active',
              },
              {
                label: t('subscriptions.checkout.stepConfirmation'),
                status: 'pending',
              },
            ]}
          />

          <CheckoutGridComponent>
            <div>
              <Ch.OrderCard>
                <Ch.OrderCardTitle>
                  {t('subscriptions.orderSummary')}
                </Ch.OrderCardTitle>
                <Ch.PlanSummaryRow>
                  <Ch.PlanIcon aria-hidden>{planEmoji}</Ch.PlanIcon>
                  <div>
                    <Ch.PlanInfoName>{displayPlanName}</Ch.PlanInfoName>
                    <Ch.PlanInfoSub>
                      {t('subscriptions.checkout.planInfoSub', {
                        billing:
                          frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.yearly')
                            : t('subscriptions.monthly'),
                        count: maxCommunities,
                        teams: t('changePlan.specCommunities'),
                      })}
                    </Ch.PlanInfoSub>
                    {isCustom ? (
                      <Ch.PlanBadge $variant="enterprise">
                        {t('changePlan.tierEnterprise')}
                      </Ch.PlanBadge>
                    ) : isCurrent ? (
                      <Ch.PlanBadge $variant="current">
                        {t('changePlan.currentPlanBadge')}
                      </Ch.PlanBadge>
                    ) : isCheckoutDetailsLoading ? (
                      <Ch.PlanBadgeSkeleton
                        className="skeleton skeleton-text"
                        aria-hidden
                      />
                    ) : isUpgradeCheckout &&
                      planChangeDirection === 'downgrade' ? (
                      <Ch.PlanBadge $variant="downgrade">
                        {t('subscriptions.checkout.badgeDowngrade', {
                          plan: fromPlanName,
                        })}
                      </Ch.PlanBadge>
                    ) : isUpgradeCheckout ? (
                      <Ch.PlanBadge $variant="upgrade">
                        {t('subscriptions.checkout.badgeUpgrade', {
                          plan: fromPlanName,
                        })}
                      </Ch.PlanBadge>
                    ) : (
                      <Ch.PlanBadge $variant="new">
                        {t('subscriptions.checkout.badgeNew')}
                      </Ch.PlanBadge>
                    )}
                  </div>
                </Ch.PlanSummaryRow>

                <Ch.SpecPills>
                  <Ch.SpecPill>
                    <span aria-hidden>🏠</span>
                    <span>
                      <Ch.SpecPillVal>{maxCommunities}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specCommunities')}
                    </span>
                  </Ch.SpecPill>
                  <Ch.SpecPill>
                    <span aria-hidden>👥</span>
                    <span>
                      {t('changePlan.upTo')}{' '}
                      <Ch.SpecPillVal>{maxMembers}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specMembers')}
                    </span>
                  </Ch.SpecPill>
                  {/* <Ch.SpecPill>
                    <span aria-hidden>📋</span>
                    <span>
                      <Ch.SpecPillVal>{maxGroups}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specSchedulers')}
                    </span>
                  </Ch.SpecPill> */}
                  <Ch.SpecPill>
                    <span aria-hidden>💾</span>
                    <span>
                      <Ch.SpecPillVal>{maxStorage} GB</Ch.SpecPillVal>{' '}
                      {t('changePlan.specStorage')}
                    </span>
                  </Ch.SpecPill>
                </Ch.SpecPills>

                <Ch.PriceBreakdown>
                  <Ch.PbRow>
                    <Ch.PbLabelCol>
                      <Ch.PbLabel>
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.checkout.planLineAnnual', {
                              plan: displayPlanName,
                            })
                          : t('subscriptions.checkout.planLineMonthly', {
                              plan: displayPlanName,
                            })}
                      </Ch.PbLabel>
                      {planLineSubtext ? (
                        <Ch.PbSubtext>{planLineSubtext}</Ch.PbSubtext>
                      ) : null}
                    </Ch.PbLabelCol>
                    <Ch.PbVal>
                      <CheckoutMoney ready={pricesReady}>
                        {currencySym}
                        {proration?.chargeAmount
                          ? formatCheckoutAmount(proration?.chargeAmount)
                          : displayedLinePrice}
                      </CheckoutMoney>
                    </Ch.PbVal>
                  </Ch.PbRow>
                  {showProrationCredit ? (
                    <Ch.PbRow>
                      <Ch.PbLabelCol>
                        <Ch.PbLabel>
                          {t('subscriptions.checkout.proratedCreditLine', {
                            plan: fromPlanName,
                          })}
                        </Ch.PbLabel>
                        {previousPlanLineSubtext ? (
                          <Ch.PbSubtext>{previousPlanLineSubtext}</Ch.PbSubtext>
                        ) : null}
                      </Ch.PbLabelCol>
                      <Ch.PbValCredit>
                        <CheckoutMoney ready={pricesReady}>
                          {proration
                            ? formatCreditAmount(proration.creditAmount)
                            : null}
                        </CheckoutMoney>
                      </Ch.PbValCredit>
                    </Ch.PbRow>
                  ) : null}
                  {showRemainingCredit ? (
                    <Ch.PbRow>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.remainingCreditLine')}
                      </Ch.PbLabel>
                      <Ch.PbValCredit>
                        <CheckoutMoney ready={pricesReady}>
                          {proration
                            ? formatRemainingCreditAmount(
                                proration.remainingCreditBalance,
                              )
                            : null}
                        </CheckoutMoney>
                      </Ch.PbValCredit>
                    </Ch.PbRow>
                  ) : null}
                  {tax > 0 ? (
                    <Ch.PbRow>
                      <Ch.PbLabel>{t('subscriptions.tax')}</Ch.PbLabel>
                      <Ch.PbVal>{tax}%</Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  {frequency === SubscriptionFrequency.YEAR &&
                  !isUpgradeCheckout &&
                  yearlySaveVsMonthly > 0 ? (
                    <Ch.PbRow $highlight>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.annualSavingLine')}
                      </Ch.PbLabel>
                      <Ch.PbVal>
                        <CheckoutMoney ready={pricesReady}>
                          {freePeriodLabel}
                        </CheckoutMoney>
                      </Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  <Ch.PbDivider />
                  <Ch.PbRowTotal>
                    <Ch.PbLabel>
                      {t('subscriptions.checkout.totalDueToday')}
                    </Ch.PbLabel>
                    <Ch.PbVal>
                      <CheckoutMoney ready={pricesReady} skeletonWidth="5.5rem">
                        {currencySym}
                        {formatCheckoutAmount(dueToday)}
                      </CheckoutMoney>
                    </Ch.PbVal>
                  </Ch.PbRowTotal>
                </Ch.PriceBreakdown>

                <Ch.IncludedSection>
                  <Ch.IncludedTitle>
                    {t('subscriptions.checkout.includedTitle')}
                  </Ch.IncludedTitle>
                  <Ch.IncludedList>
                    {includedItems.map((line, idx) => (
                      <Ch.IncludedItem key={`included-${idx}`}>
                        <Ch.IncludedCheck aria-hidden>✓</Ch.IncludedCheck>
                        {line}
                      </Ch.IncludedItem>
                    ))}
                  </Ch.IncludedList>
                </Ch.IncludedSection>

                <Ch.InfoBox>
                  <span aria-hidden>ℹ</span>
                  <span>
                    {isUpgradeCheckout
                      ? planChangeDirection === 'downgrade'
                        ? t('subscriptions.checkout.activationInfoDowngrade', {
                            plan: fromPlanName,
                          })
                        : t('subscriptions.checkout.activationInfoUpgrade', {
                            plan: fromPlanName,
                          })
                      : frequency === SubscriptionFrequency.YEAR
                      ? t('subscriptions.checkout.activationInfoStripe')
                      : t('subscriptions.checkout.activationInfoStripeMonthly')}
                  </span>
                </Ch.InfoBox>
                <Ch.Guarantee>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>🔒</Ch.GuaranteeIcon>
                    {t('subscriptions.checkout.trustSecureStripe')}
                  </Ch.GuaranteeItem>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>✕</Ch.GuaranteeIcon>
                    {t('subscriptions.checkout.trustCancel')}
                  </Ch.GuaranteeItem>
                </Ch.Guarantee>
              </Ch.OrderCard>
            </div>

            <Ch.PaymentCard>
              <Ch.PaymentCardTitle>
                {t('subscriptions.checkout.completeOrder')}
              </Ch.PaymentCardTitle>

              <Ch.PaymentMethodSection
                role="radiogroup"
                aria-label={t('subscriptions.checkout.paymentMethodLegend')}
              >
                <Ch.PaymentMethodLegend>
                  {t('subscriptions.checkout.paymentMethodLegend')}
                </Ch.PaymentMethodLegend>
                <Ch.PaymentMethodGrid>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={effectivePaymentMethod === 'direct'}
                    onClick={() => {
                      setPaymentMethod('direct');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={effectivePaymentMethod === 'direct'}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={effectivePaymentMethod === 'direct'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payDirectTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payDirectDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.PaymentBrandStrip>
                      <Ch.PaymentBrandRow>
                        <VisaIcon />
                        <MastercardIcon />
                        <SepaIcon />
                        <StripeIcon />
                      </Ch.PaymentBrandRow>
                    </Ch.PaymentBrandStrip>
                  </Ch.PaymentMethodCard>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={effectivePaymentMethod === 'invoice'}
                    $disabled={invoicePaymentDisabled}
                    disabled={invoicePaymentDisabled}
                    onClick={() => {
                      if (invoicePaymentDisabled) return;
                      setPaymentMethod('invoice');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={effectivePaymentMethod === 'invoice'}
                    aria-disabled={invoicePaymentDisabled}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={effectivePaymentMethod === 'invoice'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payInvoiceTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payInvoiceDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.InvoiceOptionStrip>
                      <InvoiceDocIcon />
                    </Ch.InvoiceOptionStrip>
                  </Ch.PaymentMethodCard>
                </Ch.PaymentMethodGrid>
              </Ch.PaymentMethodSection>

              {effectivePaymentMethod === 'direct' ? (
                <>
                  <Ch.PriceDisplay>
                    <Ch.PriceBig>
                      <Ch.PriceBigSup>{currencySym}</Ch.PriceBigSup>
                      <CheckoutMoney
                        ready={pricesReady}
                        skeletonWidth="6rem"
                        skeletonHeight={36}
                      >
                        {formatCheckoutAmount(dueToday)}
                      </CheckoutMoney>
                      <Ch.PricePeriod>
                        {isUpgradeCheckout
                          ? t('subscriptions.checkout.pricePeriodToday')
                          : frequency === SubscriptionFrequency.YEAR
                          ? t('changePlan.perYear')
                          : t('changePlan.perMonth')}
                      </Ch.PricePeriod>
                    </Ch.PriceBig>
                    {approxMonthlyEq != null && !isUpgradeCheckout ? (
                      <Ch.PriceSub>
                        {t('changePlan.approxPerMonth', {
                          price: `${currencySym}${formatCheckoutAmount(
                            approxMonthlyEq,
                          )}`,
                        })}
                      </Ch.PriceSub>
                    ) : isUpgradeCheckout ? (
                      <Ch.PriceSub>
                        {displayPlanName} · {maxCommunities}{' '}
                        {t('changePlan.specCommunities')} ·{' '}
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.yearly')
                          : t('subscriptions.monthly')}
                      </Ch.PriceSub>
                    ) : null}
                    {showProrationCredit ? (
                      <Ch.PriceSave>
                        {t('subscriptions.checkout.prorationAppliedBadge')}
                      </Ch.PriceSave>
                    ) : isUpgradeCheckout &&
                      frequency === SubscriptionFrequency.YEAR &&
                      yearlySaveVsMonthly > 0 ? (
                      <Ch.PriceSave>{priceSaveVsMonthlyLabel}</Ch.PriceSave>
                    ) : null}
                    <Ch.PriceToday>
                      <strong>
                        {t('subscriptions.checkout.chargedToday')}{' '}
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {formatCheckoutAmount(dueToday)}
                        </CheckoutMoney>
                      </strong>
                      <br />
                      {isUpgradeCheckout &&
                      frequency === SubscriptionFrequency.YEAR
                        ? t('subscriptions.checkout.fullPriceFromDate', {
                            date: renewalDateStr,
                          })
                        : t('subscriptions.checkout.renewsOn', {
                            date: renewalDateStr,
                          })}
                    </Ch.PriceToday>
                  </Ch.PriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryLabel>
                          {frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.checkout.planLineAnnual', {
                                plan: displayPlanName,
                              })
                            : t('subscriptions.checkout.planLineMonthly', {
                                plan: displayPlanName,
                              })}
                        </Ch.PaySummaryLabel>
                        {planLineSubtext ? (
                          <Ch.PaySummarySubtext>
                            {planLineSubtext}
                          </Ch.PaySummarySubtext>
                        ) : null}
                      </Ch.PaySummaryLabelCol>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {proration?.chargeAmount
                            ? formatCheckoutAmount(proration?.chargeAmount)
                            : displayedLinePrice}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    {showProrationCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabelCol>
                          <Ch.PaySummaryLabel>
                            {t('subscriptions.checkout.proratedCreditSummary', {
                              plan: fromPlanName,
                            })}
                          </Ch.PaySummaryLabel>
                          {previousPlanLineSubtext ? (
                            <Ch.PaySummarySubtext>
                              {previousPlanLineSubtext}
                            </Ch.PaySummarySubtext>
                          ) : null}
                        </Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatCreditAmount(proration.creditAmount)
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    {showRemainingCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabel>
                          {t('subscriptions.checkout.remainingCreditSummary')}
                        </Ch.PaySummaryLabel>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatRemainingCreditAmount(
                                  proration.remainingCreditBalance,
                                )
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {isUpgradeCheckout
                          ? t('subscriptions.checkout.totalDueToday')
                          : t('subscriptions.total')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {formatCheckoutAmount(dueToday)}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {payActionsBlock}

                  <Ch.StripeNote>
                    <span>
                      {t('subscriptions.checkout.stripeProcessedNote')}
                    </span>
                    <StripeWordmark height={16} />
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>✕</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustCancelDetailed')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🔒</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustSSL')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              ) : (
                <>
                  <Ch.InvoicePriceDisplay>
                    <Ch.InvoicePriceTitle>
                      {t('subscriptions.checkout.invoiceHeadline')}
                    </Ch.InvoicePriceTitle>
                    {frequency !== SubscriptionFrequency.YEAR ? (
                      <Ch.InvoiceYearlyOnlyBanner role="status">
                        {t('subscriptions.checkout.invoiceYearlyOnly')}
                      </Ch.InvoiceYearlyOnlyBanner>
                    ) : null}
                  </Ch.InvoicePriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryLabel>
                          {frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.checkout.planLineAnnual', {
                                plan: displayPlanName,
                              })
                            : t('subscriptions.checkout.planLineMonthly', {
                                plan: displayPlanName,
                              })}
                        </Ch.PaySummaryLabel>
                        {planLineSubtext ? (
                          <Ch.PaySummarySubtext>
                            {planLineSubtext}
                          </Ch.PaySummarySubtext>
                        ) : null}
                      </Ch.PaySummaryLabelCol>
                      <Ch.PaySummaryVal>
                        <CheckoutMoney ready={pricesReady}>
                          {currencySym}
                          {proration?.chargeAmount
                            ? formatCheckoutAmount(proration?.chargeAmount)
                            : displayedLinePrice}
                        </CheckoutMoney>
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    {showProrationCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabelCol>
                          <Ch.PaySummaryLabel>
                            {t('subscriptions.checkout.proratedCreditSummary', {
                              plan: fromPlanName,
                            })}
                          </Ch.PaySummaryLabel>
                          {previousPlanLineSubtext ? (
                            <Ch.PaySummarySubtext>
                              {previousPlanLineSubtext}
                            </Ch.PaySummarySubtext>
                          ) : null}
                        </Ch.PaySummaryLabelCol>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatCreditAmount(proration.creditAmount)
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    {showRemainingCredit ? (
                      <Ch.PaySummaryRow>
                        <Ch.PaySummaryLabel>
                          {t('subscriptions.checkout.remainingCreditSummary')}
                        </Ch.PaySummaryLabel>
                        <Ch.PaySummaryValCredit>
                          <CheckoutMoney ready={pricesReady}>
                            {proration
                              ? formatRemainingCreditAmount(
                                  proration.remainingCreditBalance,
                                )
                              : null}
                          </CheckoutMoney>
                        </Ch.PaySummaryValCredit>
                      </Ch.PaySummaryRow>
                    ) : null}
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {t('subscriptions.checkout.totalDueToday')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {isUpgradeCheckout ? (
                          <CheckoutMoney ready={pricesReady}>
                            {currencySym}
                            {formatCheckoutAmount(dueToday)}
                          </CheckoutMoney>
                        ) : (
                          t('subscriptions.checkout.toBeInvoiced')
                        )}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {showInvoiceForm ? (
                    <Ch.InvoiceForm
                      onSubmit={submitInvoiceSubscription}
                      noValidate
                    >
                      {requiresChfInvoiceBilling ? (
                        <InvoiceChfBillingFields
                          ref={invoiceBillingRef}
                          active={requiresChfInvoiceBilling}
                          defaultCountry="CH"
                          defaults={invoiceBillingDefaults}
                          onReadyChange={setInvoiceBillingReady}
                        />
                      ) : (
                        <Ch.InvoiceFormSection>
                          <InvoicePayerEmailsField
                            ref={payerEmailsFieldRef}
                            emails={payerEmailRows}
                            onChange={setPayerEmailRows}
                            inputId="payer-email"
                            label={t('subscriptions.checkout.chf.billingEmail')}
                            hint={t(
                              'subscriptions.checkout.chf.recipientsHint',
                              {
                                email: profile?.email ?? '',
                              },
                            )}
                            placeholder={t(
                              'subscriptions.checkout.invoiceEmailPlaceholder',
                            )}
                            optional
                          />
                        </Ch.InvoiceFormSection>
                      )}
                      <Ch.InvoiceFormActions>
                        {invoiceError ? (
                          <Ch.InvoiceFormError role="alert">
                            {invoiceError}
                          </Ch.InvoiceFormError>
                        ) : null}
                        <Ch.BtnPurchase
                          type="submit"
                          disabled={invoiceSubmitting}
                        >
                          {requiresChfInvoiceBilling ? null : (
                            <span aria-hidden>📋</span>
                          )}
                          {invoiceSubmitting
                            ? t('subscriptions.checkout.invoiceSubmitting')
                            : requiresChfInvoiceBilling
                            ? t('subscriptions.checkout.chf.submit')
                            : t('subscriptions.checkout.invoiceSubmit')}
                        </Ch.BtnPurchase>
                      </Ch.InvoiceFormActions>
                    </Ch.InvoiceForm>
                  ) : null}

                  <Ch.StripeNote as="div">
                    {requiresChfInvoiceBilling
                      ? t('subscriptions.checkout.chf.activationNote')
                      : t('subscriptions.checkout.invoicePageNote')}
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>📧</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust1')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🏦</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust2')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              )}
            </Ch.PaymentCard>
          </CheckoutGridComponent>
        </CheckoutPageWrapComponent>
      </Ch.CheckoutBackdrop>
      <CommunitySlotsModal
        isOpen={communitySlotsOpen}
        onClose={onCommunitySlotsClose}
        maxSlots={Math.max(0, maxCommunities ?? 0)}
        userId={profile?.userId ?? ''}
        communities={ownedCommunitiesList}
        savedSelection={pendingSlotSelection}
        onSaved={onCommunitySlotsSavedCheckout}
      />
    </OutsideAppPageContentWrapper>
  );
};

export default SubscriptionInner;
